import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { createApiRequest } from '../../utils/apiUtils';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

interface Eligibility {
  canAdd: boolean;
  storeCount: number;
  paidStoreCount: number;
  exempt: boolean;
  maxStores: number | null;
}

interface StoreForm {
  storeName: string;
  managerLastName: string;
  managerFirstName: string;
  postalCode: string;
  address: string;
  phone: string;
}

const EMPTY_FORM: StoreForm = {
  storeName: '',
  managerLastName: '',
  managerFirstName: '',
  postalCode: '',
  address: '',
  phone: '',
};

const extractMessage = (e: unknown): string => {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const j = JSON.parse(raw);
    if (j && typeof j.message === 'string') return j.message;
  } catch {
    /* not JSON */
  }
  return raw || '操作に失敗しました。';
};

const POSTAL_RE = /^[0-9]{7}$|^[0-9]{3}-[0-9]{4}$/;
const PHONE_RE = /^(?:0(?:\d-?){8,9}\d|\+81-?[1-9](?:-?\d){8,9})$/;

/**
 * 店舗の追加（同一企業内）。feast-fe ADR 0017。
 * BE: GET /stores/{storeId}/store-creation/eligibility, POST /stores/{storeId}/store-creation。
 * 追加できるのは「課金中の店舗数 + 1」まで（EXEMPT は無制限）。条件を満たさない場合はボタンを無効化し注記を表示。
 */
const StoreCreationSection: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const isManager = localStorage.getItem('role') === 'ROLE_MANAGER';

  const [eligibility, setEligibility] = useState<Eligibility | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<StoreForm>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  const fetchEligibility = useCallback(() => {
    if (!store_id) return;
    setLoading(true);
    createApiRequest<Eligibility>(`/stores/${store_id}/store-creation/eligibility`, { method: 'GET' })
      .then((data) => setEligibility(data))
      .catch((e) => setError(extractMessage(e)))
      .finally(() => setLoading(false));
  }, [store_id]);

  useEffect(() => {
    if (isManager) fetchEligibility();
    else setLoading(false);
  }, [isManager, fetchEligibility]);

  const setField = (key: keyof StoreForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const validate = (f: StoreForm): string | null => {
    if (!f.storeName.trim() || !f.managerLastName.trim() || !f.managerFirstName.trim() || !f.address.trim()) {
      return 'すべての項目を入力してください。';
    }
    if (!POSTAL_RE.test(f.postalCode.trim())) return '郵便番号の形式が正しくありません（例: 150-0001）。';
    if (!PHONE_RE.test(f.phone.trim())) return '電話番号の形式が正しくありません（例: 03-1234-5678）。';
    return null;
  };

  const submit = () => {
    if (!store_id) return;
    const v = validate(form);
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setBusy(true);
    createApiRequest(`/stores/${store_id}/store-creation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    })
      .then(() => {
        setSuccess(true);
        setOpen(false);
        setForm(EMPTY_FORM);
        fetchEligibility();
      })
      .catch((e) => setError(extractMessage(e)))
      .finally(() => setBusy(false));
  };

  if (!isManager) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const canAdd = eligibility?.canAdd ?? false;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        店舗を追加
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          店舗を追加しました。追加した店舗はログインし直して店舗選択から利用できます。
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          現在の企業に店舗を追加します（追加する店舗は30日間の無料トライアルで開始します）。
        </Typography>
        {/* ADR 0078（feast-be）: 企業内に課金中(ACTIVE)のサブスクが1つ以上あれば追加可（上限なし）。 */}
        {!canAdd && (
          <Alert severity="info" sx={{ mb: 2 }}>
            この企業のいずれかの店舗でサブスクリプション（課金）を有効化すると、店舗を追加できるようになります
            {eligibility && `（この企業: ${eligibility.storeCount} 店舗 / 課金中 ${eligibility.paidStoreCount} 店舗）`}。
          </Alert>
        )}
        <Button variant="contained" disabled={!canAdd || busy} onClick={() => setOpen(true)}>
          店舗を追加
        </Button>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>店舗を追加</DialogTitle>
        <DialogContent>
          <TextField label="店舗名" value={form.storeName} onChange={setField('storeName')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="店舗責任者 氏（姓）" value={form.managerLastName} onChange={setField('managerLastName')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} />
            <TextField label="店舗責任者 名" value={form.managerFirstName} onChange={setField('managerFirstName')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 50 }} />
          </Box>
          <TextField label="郵便番号（例: 150-0001）" value={form.postalCode} onChange={setField('postalCode')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 8 }} />
          <TextField label="店舗住所" value={form.address} onChange={setField('address')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 255 }} />
          <TextField label="電話番号（例: 03-1234-5678 / +81-90-1234-5678）" value={form.phone} onChange={setField('phone')} fullWidth margin="dense" size="small" inputProps={{ maxLength: 20 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            ※ 店舗の電話番号は、予約確認などお客様・スタッフ向けに送信するメールに記載されます。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setOpen(false)} disabled={busy}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={submit} disabled={busy}>
            追加する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreCreationSection;
